import * as Sentry from '@sentry/nextjs';
import { RpcError } from '@dealroadshow/json-rpc-dispatcher';

interface ILogParams {
  message: string,
  error?: unknown,
  context?: Record<string, unknown>,
}

type TSeverity = 'info' | 'debug' | 'warn' | 'error';

const sentryLogLevels: Partial<Record<TSeverity, Sentry.SeverityLevel>> = {
  warn: 'warning',
  error: 'error',
};

function captureLog(
  severity: TSeverity,
  params: ILogParams,
  caller: (...args: never[]) => void,
) {
  const { message, error, context } = params;
  const logArgs = [message, error, context].filter(Boolean);

  // eslint-disable-next-line no-console
  console[severity](...logArgs);

  if (sentryLogLevels[severity]) {
    captureExternalLog(sentryLogLevels[severity], params, caller);
  }
}

/**
 * Builds an Error for Sentry with stack trace pointing to the Logger.xxx() call site,
 * not to Logger internals. Uses Error.captureStackTrace to strip Logger frames.
 */
function getException(
  message: string,
  caller: (...args: never[]) => void,
  error?: unknown,
): Error {
  if (error instanceof RpcError) {
    const rpcErrorData = error.error as Record<string, any>;
    const rpcMethod = (rpcErrorData?._jsonRpcRequestData as Record<string, unknown>)?.method ?? 'Unknown method';
    const rpcMessage = rpcErrorData?.message ?? '-';

    const exception = new Error(
      `JsonRpcError Method: ${ rpcMethod } Message: ${ rpcMessage }`,
    );
    if (Error.captureStackTrace) {
      Error.captureStackTrace(exception, caller);
    }

    return exception;
  }

  if (error instanceof Error) {
    return error;
  }

  const exception = new Error(message);
  if (Error.captureStackTrace) {
    Error.captureStackTrace(exception, caller);
  }

  return exception;
}

function captureExternalLog(
  level: Sentry.SeverityLevel,
  { message, error, context }: ILogParams,
  caller: (...args: never[]) => void,
) {
  return Sentry.captureException(getException(message, caller, error), {
    level,
    extra: {
      'Log message': message,
// @ts-ignore TODO: FIN-32798 strictNullChecks
      ...(error && {
        'Original error': error,
      }),
      ...(context && Object.keys(context).length > 0 && {
        'Error context': context,
      }),
    },
  });
}

class Logger {
  static log(params: ILogParams) {
    captureLog('info', params, this.log);
  }

  static debug(params: ILogParams) {
    captureLog('debug', params, this.debug);
  }

  static info(params: ILogParams) {
    captureLog('info', params, this.info);
  }

  static warn(params: ILogParams) {
    captureLog('warn', params, this.warn);
  }

  static error(params: ILogParams) {
    captureLog('error', params, this.error);
  }
}

export default Logger;
